import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useAgents, useSuspendAgent, useActivateAgent } from "@/hooks/use-agents";
import {
  IconPlus,
  IconRefresh,
  IconSearch,
  IconUsers,
  IconPhone,
  IconMail,
  IconHash,
  IconDots,
  IconDownload,
} from "@tabler/icons-react";
import { DataExportDialog } from "@/components/shared/data-export-dialog";
import { CreateAgentDialog } from "@/components/agents/create-agent-dialog";
import { useExportAgents } from "@/hooks/use-data-export";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import type { AgentParams } from "@/types/api";
import { formatDistanceToNow } from "date-fns";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/agents/")({
  component: AgentsPage,
});

function AgentsPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <AgentsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentsContent() {
  const [filters, setFilters] = useState<AgentParams>({
    page: 1,
    limit: 50,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { data, isLoading, refetch } = useAgents(filters);
  const suspendAgent = useSuspendAgent();
  const activateAgent = useActivateAgent();
  const exportAgents = useExportAgents();
  const navigate = useNavigate();

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters((prev) => ({ ...prev, search: value || undefined, page: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value);
    setFilters((prev) => ({
      ...prev,
      status: value === "all" ? undefined : (value as any),
      page: 1,
    }));
  };

  const handleSuspend = async (agentId: string, agentName: string) => {
    try {
      await suspendAgent.mutateAsync(agentId);
      toast.success(`Agent ${agentName} suspended successfully`);
    } catch (error: any) {
      toast.error(error.message || "Failed to suspend agent");
    }
  };

  const handleActivate = async (agentId: string, agentName: string) => {
    try {
      await activateAgent.mutateAsync(agentId);
      toast.success(`Agent ${agentName} activated successfully`);
    } catch (error: any) {
      toast.error(error.message || "Failed to activate agent");
    }
  };

  const handleViewDetails = (agentId: string) => {
    // Navigate to agent details page
    navigate({ to: "/agents/$id", params: { id: agentId } });
  };

  const handleViewSims = (agentId: string) => {
    // Navigate to agent's SIMs page
    navigate({ to: "/agents/$id/sims", params: { id: agentId } });
  };

  const handleEdit = (agentId: string) => {
    // Navigate to edit agent page
    navigate({ to: "/agents/$id/edit", params: { id: agentId } });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "suspended":
        return "secondary";
      case "inactive":
        return "outline";
      default:
        return "outline";
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Agent Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage agents and their SIM cards
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
          >
            <IconDownload className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <IconPlus className="h-4 w-4 mr-2" />
            Add Agent
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, email, or serial number..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={handleStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      {data && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Total Agents
              </p>
              <IconUsers className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {data.pagination.total}
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Active Agents
              </p>
              <IconUsers className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {data.data.filter((a: any) => a.agent && a.agent.status === "active").length}
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Total SIMs
              </p>
              <IconPhone className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {/* {data.data.reduce((acc, a) => acc + a.stats.totalSims, 0)} */}
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">
                Total Commissions
              </p>
              <IconHash className="h-4 w-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              ৳
              {data.data
                .reduce((acc: number, a: any) => acc + parseFloat(a.agent?.commissionDue || '0'), 0)
                .toLocaleString("en-BD", { maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent</TableHead>
              <TableHead>Serial Number</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Commission Rate</TableHead>
              <TableHead>SIMs</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Total Commission</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  Loading agents...
                </TableCell>
              </TableRow>
            ) : !data || data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8">
                  No agents found
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((agentData: any) => {
                const agent = agentData.agent;
                const user = agentData.user;
                return (
                <TableRow key={agent.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{agent.agentName}</div>
                      <div className="text-sm text-muted-foreground">
                        @{user?.username}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{agent.serialNumber}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <IconMail className="h-3 w-3" />
                        {user?.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{parseFloat(agent.commissionRate || '0').toFixed(1)}%</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>N/A</div>
                      <div className="text-muted-foreground">Active</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>N/A</div>
                      <div className="text-muted-foreground">Total</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    ৳
                    {parseFloat(agent.commissionDue || '0').toLocaleString("en-BD", {
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(agent.status)}>
                      {agent.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {agent.createdAt
                      ? formatDistanceToNow(new Date(agent.createdAt), {
                          addSuffix: true,
                        })
                      : "Unknown"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <IconDots className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetails(agent.id)}>
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewSims(agent.id)}>
                          View SIMs
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(agent.id)}>
                          Edit
                        </DropdownMenuItem>
                        {agent.status === "active" ? (
                          <DropdownMenuItem
                            onClick={() =>
                              handleSuspend(agent.id, agent.agentName)
                            }
                          >
                            Suspend
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() =>
                              handleActivate(agent.id, agent.agentName)
                            }
                          >
                            Activate
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )})
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(filters.page! - 1) * filters.limit! + 1} to{" "}
            {Math.min(
              filters.page! * filters.limit!,
              data.pagination.total,
            )}{" "}
            of {data.pagination.total} agents
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === 1}
              onClick={() =>
                setFilters((prev) => ({ ...prev, page: prev.page! - 1 }))
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={filters.page === data.pagination.totalPages}
              onClick={() =>
                setFilters((prev) => ({ ...prev, page: prev.page! + 1 }))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <DataExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        title="Export Agents"
        description="Export agent data including SIMs, commissions, and statistics"
        onExport={async (params) => exportAgents.mutateAsync(params) as unknown as Promise<Blob>}
        filename="agents"
      />

      <CreateAgentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
